import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import TestSetup from './pages/TestSetup';
import PVTTest from './pages/PVTTest';
import Results from './pages/Results';
import LandingPage from './pages/LandingPage';
import AdminDashboard from './pages/AdminDashboard';
import { ROUTES } from './config/routes';
import { getUserDashboardPath } from './utils/userTypeDetection';
import GuestInfo from './pages/GuestInfo';
import GuestTest from './pages/GuestTest';
import GuestResults from './pages/GuestResults';
import KioskStart from './pages/KioskStart';
import KioskTest from './pages/KioskTest';
import KioskResults from './pages/KioskResults';
import ErrorBoundary from './components/ErrorBoundary';
import { 
  AdminErrorBoundary, 
  GuestErrorBoundary,
  AuthErrorBoundary 
} from './components/UserTypeErrorBoundaries';
import { FullScreenLoading } from './components/LoadingStates';
import PerformanceOptimizer from './components/PerformanceOptimizer';
import { CriticalResourcePreloader } from './components/AssetPreloader';


// User Type Detection Component
const UserTypeRedirect = () => {
  const { isAuthenticated, userRole, loading, user } = useAuth();
  
  console.log('🔀 UserTypeRedirect - Auth state:', { isAuthenticated, userRole, loading, user: user?.name });
  
  // Avoid redirecting away while role is still resolving after login
  if (loading || (isAuthenticated && !userRole)) {
    console.log('⏳ UserTypeRedirect - Still loading');
    return (
      <FullScreenLoading
        title="Fatigue Check"
        message="Checking authentication status..."
        showBackground={true}
      />
    );
  }
  
  if (!isAuthenticated) {
    console.log('❌ UserTypeRedirect - Not authenticated, redirecting to home');
    return <Navigate to={ROUTES.HOME} replace />;
  }
  
  // Redirect based on user role using utility function
  const dashboardPath = getUserDashboardPath(userRole === 'user' ? 'admin' : userRole);
  console.log('✅ UserTypeRedirect - Authenticated, redirecting to:', dashboardPath);
  return <Navigate to={dashboardPath} replace />;
};

// App Routes Component (needs to be inside AuthProvider)
const AppRoutes = () => {
  const { user } = useAuth();
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path={ROUTES.HOME} element={
        <ErrorBoundary>
          <LandingPage />
        </ErrorBoundary>
      } />
      <Route path={ROUTES.LOGIN} element={
        <AuthErrorBoundary>
          <Login />
        </AuthErrorBoundary>
      } />
      
      {/* Guest Routes - No authentication required */}
      <Route path={ROUTES.GUEST.INFO} element={
        <GuestErrorBoundary>
          <GuestInfo />
        </GuestErrorBoundary>
      } />
      <Route path="/guest/test/:sessionId" element={
        <GuestErrorBoundary>
          <GuestTest />
        </GuestErrorBoundary>
      } />
      <Route path="/guest/results/:sessionId" element={
        <GuestErrorBoundary>
          <GuestResults />
        </GuestErrorBoundary>
      } />

      {/* Kiosk Routes - No authentication required */}
      <Route path="/kiosk" element={<Navigate to={ROUTES.KIOSK.START} replace />} />
      <Route path={ROUTES.KIOSK.START} element={
        <ErrorBoundary>
          <KioskStart />
        </ErrorBoundary>
      } />
      <Route path="/kiosk/test/:testId" element={
        <ErrorBoundary>
          <KioskTest />
        </ErrorBoundary>
      } />
      <Route path="/kiosk/results/:testId" element={
        <ErrorBoundary>
          <KioskResults />
        </ErrorBoundary>
      } />
      
      {/* User Type Detection and Redirect */}
      <Route path={ROUTES.DASHBOARD_REDIRECT} element={
        <AuthErrorBoundary>
          <UserTypeRedirect />
        </AuthErrorBoundary>
      } />

      {/* Admin Dashboard - Standalone without Layout */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute requiredRole="admin">
          <AdminErrorBoundary
            adminId={user?.id}
            adminName={user?.name}
            companyId={user?.companyId}
          >
            <AdminDashboard />
          </AdminErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute requiredRole="admin">
          <AdminErrorBoundary
            adminId={user?.id}
            adminName={user?.name}
            companyId={user?.companyId}
          >
            <AdminDashboard />
          </AdminErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/admin/configuration" element={
        <ProtectedRoute requiredRole="admin">
          <AdminErrorBoundary
            adminId={user?.id}
            adminName={user?.name}
            companyId={user?.companyId}
          >
            <AdminDashboard />
          </AdminErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute requiredRole="admin">
          <AdminErrorBoundary
            adminId={user?.id}
            adminName={user?.name}
            companyId={user?.companyId}
          >
            <AdminDashboard />
          </AdminErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/admin/profile" element={
        <ProtectedRoute requiredRole="admin">
          <AdminErrorBoundary
            adminId={user?.id}
            adminName={user?.name}
            companyId={user?.companyId}
          >
            <AdminDashboard />
          </AdminErrorBoundary>
        </ProtectedRoute>
      } />

      {/* Other protected admin routes use their own focused page shell */}
      <Route path={ROUTES.ADMIN.BASE} element={
        <ProtectedRoute requiredRole="admin">
          <AdminErrorBoundary
            adminId={user?.id}
            adminName={user?.name}
            companyId={user?.companyId}
          >
            <Outlet />
          </AdminErrorBoundary>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to={ROUTES.ADMIN.DASHBOARD} replace />} />
        <Route path="users" element={<Navigate to={ROUTES.ADMIN.CONFIGURATION} replace />} />
        <Route path="test" element={<TestSetup />} />
        <Route path="test/:testId" element={<PVTTest />} />
        <Route path="results/:testId" element={<Results />} />
      </Route>

      {/* Legacy user routes redirect to admin dashboard */}
      <Route path="/user/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/user/*" element={<Navigate to="/admin/dashboard" replace />} />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <CriticalResourcePreloader>
        <PerformanceOptimizer 
          enableAutoOptimization={true}
          showPerformanceIndicator={process.env.NODE_ENV === 'development'}
        >
          <AuthProvider>
            <Router>
              <AppRoutes />
            </Router>
          </AuthProvider>
        </PerformanceOptimizer>
      </CriticalResourcePreloader>
    </ErrorBoundary>
  );
}

export default App;
